import { isUuid } from '@leanin/shared';
import type { GroupKind } from '@/lib/group-url';
import { readStorage, removeStorage, writeStorage } from '@/lib/browser-storage';
import { parseReturnPath } from '@/lib/return-path';

// Where a new member lands once onboarding finishes, carried across the
// onboarding flow. Written at /auth/finishing (a join-link group, a "Sign up
// to Join" intent, or the page the member was on when they chose to join —
// the `?next=` a shared link's sign-in bounce or a Join CTA carried) and by
// the join-a-network landing; read once by the onboarding completion step.
// Cleared on EVERY onboarding finish (not just when read on the happy path),
// and overwritten or cleared by every signup's /auth/finishing, so a value
// from an abandoned signup can't leak into a later one in the same tab — the
// pendingInviteToken equivalent of that leak already bit us.
//
// The storage key keeps its original name: values written before the
// destination grew a `path` variant (open tabs across a deploy) still parse
// as the group variant.
const POST_ONBOARDING_DESTINATION_KEY = 'onboarding.postOnboardingGroup';

export interface PostOnboardingGroup {
  readonly kind: GroupKind;
  readonly id: string;
  // The post-signup auto-join was refused with `groups/intake_required`, so
  // the completion step must land the member in the group's Request-to-Join
  // modal (`?join=1`) instead of assuming a pending request already exists.
  readonly needsJoin?: true;
  // The group came from a "Sign up to Join" click on its own page (the
  // pendingJoinGroup intent) rather than a join-link claim. That concrete
  // intent outranks the journey's directory landing at the completion step —
  // the member asked for this group, not for a browse surface.
  readonly fromIntent?: true;
}

// An in-app page the member asked for before signing up (validated with
// `parseReturnPath` on write and again on read — sessionStorage is not a
// trust boundary we control).
export interface PostOnboardingPath {
  readonly kind: 'path';
  readonly path: string;
}

export type PostOnboardingDestination = PostOnboardingGroup | PostOnboardingPath;

const GROUP_KINDS: ReadonlyArray<GroupKind> = ['circle', 'network', 'space'];

// What /auth/finishing stores for a member about to start onboarding. A join
// group (a join-link claim or a "Sign up to Join" click) outranks `next`: it
// names the group the member asked to join and carries what the completion
// step needs to finish that join (`needsJoin`), and the page they joined
// from is usually that group's own. Null means "nothing to land on" — the
// caller then clears whatever an abandoned earlier signup in this tab left.
export function destinationAfterSignup(input: {
  readonly group: PostOnboardingGroup | null;
  readonly next: string | undefined;
}): PostOnboardingDestination | null {
  if (input.group !== null) return input.group;
  if (input.next !== undefined) return { kind: 'path', path: input.next };
  return null;
}

export function writePostOnboardingDestination(destination: PostOnboardingDestination): void {
  if (typeof window === 'undefined') return;
  writeStorage('session', POST_ONBOARDING_DESTINATION_KEY, JSON.stringify(destination));
}

export function readPostOnboardingDestination(): PostOnboardingDestination | null {
  if (typeof window === 'undefined') return null;
  const raw = readStorage('session', POST_ONBOARDING_DESTINATION_KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    if (!(err instanceof SyntaxError)) throw err;
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const candidate = parsed as { kind?: unknown; id?: unknown; path?: unknown };
  if (candidate.kind === 'path') {
    if (typeof candidate.path !== 'string') return null;
    const path = parseReturnPath(candidate.path);
    return path === null ? null : { kind: 'path', path };
  }
  const withFlag = parsed as { needsJoin?: unknown; fromIntent?: unknown };
  if (
    typeof candidate.kind === 'string' &&
    (GROUP_KINDS as ReadonlyArray<string>).includes(candidate.kind) &&
    typeof candidate.id === 'string' &&
    isUuid(candidate.id)
  ) {
    return {
      kind: candidate.kind as GroupKind,
      id: candidate.id,
      ...(withFlag.needsJoin === true ? { needsJoin: true as const } : {}),
      ...(withFlag.fromIntent === true ? { fromIntent: true as const } : {}),
    };
  }
  return null;
}

export function clearPostOnboardingDestination(): void {
  if (typeof window === 'undefined') return;
  removeStorage('session', POST_ONBOARDING_DESTINATION_KEY);
}
